import { useState, useMemo, useCallback, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { ArrowLeft, Link2, Mic, MoreHorizontal, Pin, PinOff, Trash2, RefreshCw } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { NotebookNote } from '../../../shared/types/notebook'
import { getChildren } from '../notebookTree'
import RichContentEditor, { type SlashCommand } from '../RichContentEditor'
import { AudioPlayer } from '../../recording'
import { getRecordingForEntry, regenerateRecordingSummary, getRecording } from '../../recording'
import PageRow from './PageRow'
import MeetingNoteView from './MeetingNoteView'
import ImageGallery from './ImageGallery'
import AddMeetingLinkSheet from './AddMeetingLinkSheet'
import { usePageEditorSync } from '../usePageEditorSync'

interface PageContentViewProps {
  note: NotebookNote
  allNotes: NotebookNote[]
  isNew?: boolean
  /** When true, hides the back button (used in desktop two-pane layout) */
  embedded?: boolean
  onBack: () => void
  onNavigate: (id: string) => void
  onTogglePin: (id: string) => void
  onDelete: (id: string) => void
  onUpdateNote: (noteId: string, updates: Partial<NotebookNote>) => void
  onCreateChild: (parentId: string) => void
  onRecord?: () => void
  /** Reload notebook data (used after regenerating a meeting summary) */
  onRefresh?: () => void
  /** Wait for pending PATCH saves on a note to settle before reading server state */
  onFlushSaves?: (noteId: string) => Promise<void>
}

export default function PageContentView({
  note,
  allNotes,
  isNew,
  embedded,
  onBack,
  onNavigate,
  onTogglePin,
  onDelete,
  onUpdateNote,
  onCreateChild,
  onRecord,
  onRefresh,
  onFlushSaves,
}: PageContentViewProps) {
  const { t } = useTranslation('recording')
  const { t: tn } = useTranslation('notebook')
  const { t: tc } = useTranslation('common')
  const [menuOpen, setMenuOpen] = useState(false)
  const [regenerating, setRegenerating] = useState(false)
  const [addToPageOpen, setAddToPageOpen] = useState(false)
  const children = getChildren(allNotes, note.id)

  const slashCommands = useMemo<SlashCommand[]>(() => [
    { id: 'link', label: tn('slashMenu.commands.link.label'), icon: '🔗', description: tn('slashMenu.commands.link.description') },
    { id: 'new_page', label: tn('slashMenu.commands.newPage.label'), icon: '📄', description: tn('slashMenu.commands.newPage.description') },
  ], [tn])

  const {
    editTitle,
    setEditTitle,
    editContent,
    setEditContent,
    titleRef,
    handleTitleBlur,
    handleTitleKeyDown,
  } = usePageEditorSync({ note, allNotes, onUpdateNote, isNew })

  const hasRecording = useMemo(() => note.tags.includes('recording'), [note.tags])
  const isPending = useMemo(() => note.tags.includes('_pending'), [note.tags])

  // Notes-stale state: user edited the Notes tab after the AI summary was last
  // generated, so the summary may not reflect their latest input. The chip on
  // the Summary tab nudges them to regenerate.
  const summaryStale = useMemo(() => {
    if (!hasRecording) return false
    const notesAt = note.userNotesUpdatedAt
    if (!notesAt) return false
    const summaryAt = note.summaryGeneratedAt
    if (!summaryAt) return true
    return new Date(notesAt).getTime() > new Date(summaryAt).getTime()
  }, [hasRecording, note.userNotesUpdatedAt, note.summaryGeneratedAt])

  // Track recording ID + status for the regenerate/retry menu
  const recordingIdRef = useRef<string | null>(null)
  const [recordingFailed, setRecordingFailed] = useState(false)
  useEffect(() => {
    if (!hasRecording || isPending) return
    getRecordingForEntry(note.id)
      .then(r => {
        recordingIdRef.current = r.id
        setRecordingFailed(r.status === 'failed')
      })
      .catch(() => {})
  }, [note.id, hasRecording, isPending])

  const handleRegenerate = useCallback(async () => {
    const recId = recordingIdRef.current
    if (!recId || regenerating) return
    setRegenerating(true)
    try {
      // Flush any pending Notes-tab edits to the DB before regenerating, so
      // the backend sees the latest user_notes_md when building the prompt.
      await onFlushSaves?.(note.id)
      await regenerateRecordingSummary(recId)
      // Poll recording status until done
      const pollInterval = setInterval(async () => {
        try {
          const rec = await getRecording(recId)
          if (rec.status === 'done') {
            clearInterval(pollInterval)
            setRegenerating(false)
            setRecordingFailed(false)
            onRefresh?.()
          }
        } catch {
          // Keep polling
        }
      }, 3000)
    } catch {
      setRegenerating(false)
    }
  }, [regenerating, onRefresh, onFlushSaves, note.id])

  return (
    <div className="h-full flex flex-col">
      {/* Top bar */}
      <div className={`flex items-center justify-between px-4 pt-3 pb-3 ${embedded ? '' : 'border-b border-gold/10'}`}>
        {embedded ? (
          <div />
        ) : (
          <button onClick={onBack} className="flex items-center gap-1.5 text-gold text-sm font-medium">
            <ArrowLeft size={16} />
            {tc('actions.back')}
          </button>
        )}
        <div className="flex items-center gap-1">
          {onRecord && !hasRecording && (
            <button
              onClick={onRecord}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/5 transition-colors"
              title={tn('page.recordButton')}
            >
              <Mic size={16} className="text-gold/60" />
            </button>
          )}
          <button
            onClick={() => setMenuOpen(v => !v)}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/5 transition-colors"
          >
            <MoreHorizontal size={18} className="text-pearl/60" />
          </button>
          <AnimatePresence>
            {menuOpen && (
              <>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 z-40"
                  onClick={() => setMenuOpen(false)}
                />
                <motion.div
                  initial={{ opacity: 0, scale: 0.95, y: -4 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: -4 }}
                  transition={{ duration: 0.12 }}
                  className="absolute right-0 top-10 w-44 bg-charcoal border border-gold/10 rounded-xl shadow-xl z-50 overflow-hidden"
                >
                  <button
                    onClick={() => { setMenuOpen(false); onTogglePin(note.id) }}
                    className="flex items-center gap-3 w-full px-4 py-3 text-sm text-pearl hover:bg-white/5 transition-colors"
                  >
                    {note.pinned ? <PinOff size={14} /> : <Pin size={14} />}
                    {note.pinned ? tn('page.unpin') : tn('page.pin')}
                  </button>
                  {hasRecording && !isPending && (
                    <button
                      onClick={() => { setMenuOpen(false); handleRegenerate() }}
                      disabled={regenerating}
                      className="flex items-center gap-3 w-full px-4 py-3 text-sm text-pearl hover:bg-white/5 transition-colors disabled:opacity-40"
                    >
                      <RefreshCw size={14} className={regenerating ? 'animate-spin' : ''} />
                      {regenerating
                        ? t(recordingFailed ? 'regenerate.retryLoading' : 'regenerate.loading')
                        : t(recordingFailed ? 'regenerate.retryButton' : 'regenerate.button')}
                    </button>
                  )}
                  <button
                    onClick={() => { setMenuOpen(false); onDelete(note.id) }}
                    className="flex items-center gap-3 w-full px-4 py-3 text-sm text-rose hover:bg-rose/5 transition-colors"
                  >
                    <Trash2 size={14} /> {tc('actions.delete')}
                  </button>
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Regeneration banner — always visible above the scroll area so it's
          impossible to miss, regardless of which tab or scroll position. */}
      {regenerating && hasRecording && !isPending && (
        <div className="flex items-center gap-3 bg-gold/15 border-b border-gold/25 px-5 sm:px-8 lg:px-16 py-2.5">
          <RefreshCw size={14} className="animate-spin text-gold shrink-0" />
          <span className="text-ivory text-sm font-medium">
            {t(recordingFailed ? 'regenerate.retryLoading' : 'regenerate.loading')}
          </span>
        </div>
      )}

      {/* Page content — scrollable */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden px-5 sm:px-8 lg:px-16 py-6">
        <div className="max-w-3xl mx-auto">
        {/* Icon + Title (inline editable) */}
        <div className="mb-6">
          <span className="text-5xl block mb-3">{note.icon}</span>
          <input
            ref={titleRef}
            type="text"
            value={editTitle}
            onChange={e => setEditTitle(e.target.value)}
            onBlur={handleTitleBlur}
            onKeyDown={handleTitleKeyDown}
            placeholder={tn('page.untitledPlaceholder')}
            className="w-full bg-transparent font-display text-2xl text-ivory font-semibold leading-tight placeholder:text-pearl/45 focus:outline-none"
          />
        </div>

        {/* Audio player for recording notes */}
        {hasRecording && !isPending && <AudioPlayer entryId={note.id} />}

        {/* Pending recording status */}
        {isPending && (
          <div className="flex items-center gap-3 bg-gold/5 border border-gold/10 rounded-xl px-4 py-3 mb-4">
            <span className="inline-block w-4 h-4 border-2 border-gold/40 border-t-gold rounded-full animate-spin" />
            <span className="text-pearl/60 text-sm">{t('pending.description')}</span>
          </div>
        )}

        {/* Content body — tabbed for recordings, editor for everything else */}
        <div className={children.length > 0 ? 'mb-6' : ''}>
          {hasRecording && !isPending ? (
            <MeetingNoteView
              note={note}
              allNotes={allNotes}
              onNavigate={onNavigate}
              onUpdateNote={onUpdateNote}
              summaryStale={summaryStale}
              regenerating={regenerating}
              onRegenerate={handleRegenerate}
            />
          ) : !isPending ? (
            <RichContentEditor
              value={editContent}
              onChange={setEditContent}
              allNotes={allNotes}
              onNavigate={onNavigate}
              slashCommands={slashCommands}
              onExecuteCommand={(id) => {
                if (id === 'new_page') onCreateChild(note.id)
              }}
              className="text-pearl/80 text-sm leading-relaxed"
              minHeight="60px"
            />
          ) : null}
        </div>

        {/* Image gallery */}
        <ImageGallery entryId={note.id} isTemp={note.id.startsWith('note-')} />

        {/* Add meeting to another page */}
        {hasRecording && !isPending && (
          <div className="mt-4 mb-6">
            <button
              onClick={() => setAddToPageOpen(true)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gold/15 text-pearl/70 hover:text-ivory hover:border-gold/30 hover:bg-white/[0.03] transition-colors text-sm"
            >
              <Link2 size={14} className="text-gold/70" />
              {tn('addToPage.button')}
            </button>
          </div>
        )}

        {/* Child pages */}
        {children.length > 0 && (
          <div>
            {children.map(child => (
              <PageRow
                key={child.id}
                note={child}
                depth={0}
                hasChildren={(child.childCount ?? 0) > 0}
                onNavigate={onNavigate}
                onCreateChild={onCreateChild}
              />
            ))}
          </div>
        )}
        </div>
      </div>

      <AddMeetingLinkSheet
        open={addToPageOpen}
        onClose={() => setAddToPageOpen(false)}
        notes={allNotes}
        meetingId={note.id}
        meetingTitle={note.title || tn('page.untitledPlaceholder')}
        onUpdateNote={onUpdateNote}
      />
    </div>
  )
}
